// pages/main/products/page.tsx

import { productService } from '@cabukaka/services'
import { Product } from '@cabukaka/types'
import { ProductsList } from '../../../components/shared/products/ProductsList'
import PageHeader from '../../../components/ui/PageHeader'
import { Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { PageContainer } from '../../../components/ui/PageContainer'

export default function ProductsPage() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const query = searchParams.get('q') || ''
    const page = parseInt(searchParams.get('page') || '1')

    const [products, setProducts] = useState<Product[]>([])
    const [loading, setLoading] = useState(true)
    const [totalPages, setTotalPages] = useState(1)
    const [searchInput, setSearchInput] = useState(query)

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true)
            try {
                let result
                if (query) {
                    result = await productService.searchProducts(query, page)
                } else {
                    result = await productService.getProducts(page)
                }
                setProducts(result.data || result)
                setTotalPages(result.pages || Math.ceil((result.total || 0) / 20))
            } catch (error) {
                console.error(error)
                setProducts([])
            } finally {
                setLoading(false)
            }
        }

        fetchProducts()
    }, [query, page])

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        if (searchInput.trim()) {
            navigate(`/produits?q=${encodeURIComponent(searchInput)}&page=1`)
        } else {
            navigate('/produits?page=1')
        }
    }

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            if (query) {
                navigate(`/produits?q=${encodeURIComponent(query)}&page=${newPage}`)
            } else {
                navigate(`/produits?page=${newPage}`)
            }
        }
    }

    return (
        <PageContainer className="min-h-screen bg-gray-50">
            <PageHeader
                breadcrumb={[
                    { label: "Produits", href: "/produits" }
                ]}
                showBackButton={false}
            />

            <div className="container mx-auto px-4 py-6 md:py-8">
                <div className="mb-6 md:mb-8">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        {query ? `Résultats pour "${query}"` : 'Tous nos produits'}
                    </h1>

                    <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Rechercher un produit..."
                                className="w-full px-4 py-3 pl-11 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                            />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <button
                            type="submit"
                            className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-xl transition-colors"
                        >
                            Rechercher
                        </button>
                    </form>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">
                            Aucun produit trouvé
                        </h3>
                        <p className="text-gray-500">
                            {query ? `Aucun résultat pour "${query}"` : 'Aucun produit disponible'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mb-4 text-sm text-gray-500">
                            {products.length} produit{products.length > 1 ? 's' : ''} trouvé{products.length > 1 ? 's' : ''}
                        </div>

                        <ProductsList
                            layout="grid"
                            products={products}
                            showActionBtn={true}
                            showDescription={true}
                            showRating={true}
                            showReviewsCount={true}
                            showShopName={true}
                            showShopAddress={false}
                        />

                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 mt-8">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 1}
                                    className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum
                                        if (totalPages <= 5) {
                                            pageNum = i + 1
                                        } else if (page <= 3) {
                                            pageNum = i + 1
                                        } else if (page >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i
                                        } else {
                                            pageNum = page - 2 + i
                                        }

                                        if (pageNum > totalPages) return null

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`w-10 h-10 rounded-lg transition-colors ${page === pageNum
                                                        ? 'bg-orange-600 text-white'
                                                        : 'border border-gray-200 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        )
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === totalPages}
                                    className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </PageContainer>
    )
}